/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import { Typography, Box, Stack } from "@mui/material";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
  collapseArrow,
} from "utils/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context";

function SidenavCollapse({
  icon,
  name,
  children,
  active,
  noCollapse,
  open,
  hubNotifications,
  ...rest
}) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;

  return (
    <>
      <ListItem component="li" id={`nav-item-${name}`}>
        <MDBox
          {...rest}
          sx={(theme) => {
            const style = collapseItem(theme, {
              active,
              transparentSidenav,
              whiteSidenav,
              darkMode,
              buttonActive: rest.buttonActive,
            });
            if (rest.disabled) {
              style.width = "fit-content";
            }
            return style;
          }}
        >
          <ListItemIcon
            sx={(theme) => {
              const style = collapseIconBox(theme, {
                active,
                transparentSidenav,
                whiteSidenav,
                darkMode,
              });
              return style;
            }}
          >
            {typeof icon === "string" ? (
              <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
            ) : (
              <>
                {icon}
                {rest.notifications ? (
                  <Box
                    className="sidenav-notification-bubble"
                    sx={{
                      top: "13px",
                      right: "40px",
                      backgroundColor: "red",
                    }}
                  >
                    <Typography variant="body2" fontSize="10px" fontWeight="bold">
                      {rest.notifications}
                    </Typography>
                  </Box>
                ) : null}
                {hubNotifications ? (
                  <Stack spacing={0.5} sx={{ position: "absolute", right: "18px", top: "7px" }}>
                    {hubNotifications.winWire > 0 ? (
                      <Box
                        className="sidenav-notification-bubble"
                        sx={{
                          backgroundColor: "#FFC438",
                          position: "unset",
                        }}
                      >
                        <Typography variant="body2" fontSize="10px" fontWeight="bold">
                          {hubNotifications.winWire}
                        </Typography>
                      </Box>
                    ) : null}
                    {hubNotifications.insights > 0 ? (
                      <Box
                        className="sidenav-notification-bubble"
                        sx={{
                          backgroundColor: "#17B5F6",
                          position: "unset",
                        }}
                      >
                        <Typography variant="body2" fontSize="10px" fontWeight="bold">
                          {hubNotifications.insights}
                        </Typography>
                      </Box>
                    ) : null}
                    {hubNotifications.reports > 0 ? (
                      <Box
                        className="sidenav-notification-bubble"
                        sx={{
                          backgroundColor: "#CA89F3",
                          position: "unset",
                        }}
                      >
                        <Typography variant="body2" fontSize="10px" fontWeight="bold">
                          {hubNotifications.reports}
                        </Typography>
                      </Box>
                    ) : null}
                  </Stack>
                ) : null}
              </>
            )}
          </ListItemIcon>
          <ListItemText
            primary={name}
            sx={(theme) => {
              const style = collapseText(theme, {
                miniSidenav,
                transparentSidenav,
                whiteSidenav,
                active,
              });
              return style;
            }}
          />

          {!rest.disabled ? null : (
            <ListItemText
              primary="Coming Soon!"
              sx={(theme) => {
                const style = collapseText(theme, {
                  miniSidenav,
                  transparentSidenav,
                  whiteSidenav,
                  active,
                });
                style.color = "#ff0101";
                style["& span"] = {
                  fontWeight: "bold",
                  fontSize: "0.55rem",
                  fontFamily: "Helvetica",
                  lineHeight: 0,
                  marginLeft: "-5px",
                };
                return style;
              }}
            />
          )}

          {noCollapse ? null : (
            <Icon
              sx={(theme) =>
                collapseArrow(theme, {
                  noCollapse,
                  transparentSidenav,
                  whiteSidenav,
                  miniSidenav,
                  open,
                  active,
                  darkMode,
                })
              }
            >
              expand_less
            </Icon>
          )}
        </MDBox>
      </ListItem>
      {children && (
        <Collapse in={open} unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

// Setting default values for the props of SidenavCollapse
SidenavCollapse.defaultProps = {
  active: false,
  noCollapse: true,
  children: false,
  open: false,
};

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  children: PropTypes.node,
  active: PropTypes.bool,
  noCollapse: PropTypes.bool,
  open: PropTypes.bool,
};

export default SidenavCollapse;
